// Real-name reveal (PRD §3.7). Tap anyone's icon and this little modal says who
// they really are — "LekkerBakkie is really Sarah 🙂". Not secret, just fun.

import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { Person } from "../lib/types";
import { Avatar } from "./Avatar";

export function RevealModal({ person, onClose }: { person: Person | null; onClose: () => void }) {
  useEffect(() => {
    if (!person) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [person, onClose]);

  return (
    <AnimatePresence>
      {person ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-6 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            initial={{ scale: 0.9, y: 8, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
            className="ink-block flex w-full max-w-xs flex-col items-center gap-3 rounded-2xl bg-paper px-6 py-7 text-center"
          >
            <Avatar person={person} size={76} />
            <p className="text-lg leading-snug text-ink">
              <span className="font-display text-3xl text-citrus-deep">{person.handle}</span>
              <br />
              is really <span className="font-semibold">{person.realName}</span>{" "}
              <span aria-hidden>{person.emoji}</span>
            </p>
            <button
              onClick={onClose}
              className="stamp mt-1 text-xs text-ink-muted underline decoration-dotted underline-offset-2 hover:text-ink"
            >
              Close
            </button>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
